'use client';

import { Check, X, Target, Bot, FileText, Mic, Mail, Briefcase, Wand2, SearchCheck, ShieldCheck } from 'lucide-react';
import { ScrollReveal } from '@/components/ScrollReveal';
import type { LucideIcon } from 'lucide-react';

function FeatureValue({ value }: { value: boolean | string }) {
  if (value === true) return <Check className="w-5 h-5 text-green-600 mx-auto" />;
  if (value === false) return <X className="w-5 h-5 text-gray-300 mx-auto" />;
  return <span className="text-gray-900 font-medium">{value}</span>;
}

const features = [
  { name: 'Claim verification & provenance', free: true, pro: true, premium: true, executive: true },
  { name: 'Tailored resume generation', free: '3/month', pro: '25/month', premium: '100/month', executive: 'Unlimited' },
  { name: 'Cover letter generation', free: '1/month', pro: '25/month', premium: '100/month', executive: 'Unlimited' },
  { name: 'ATS match scoring', free: 'Basic', pro: 'Advanced', premium: 'Advanced', executive: 'Advanced' },
  { name: 'Keyword gap analysis', free: false, pro: true, premium: true, executive: true },
  { name: 'Resume auto-improve', free: true, pro: true, premium: true, executive: true },
  { name: 'Resume quality scoring', free: true, pro: true, premium: true, executive: true },
  { name: 'Federal/government resumes', free: true, pro: true, premium: true, executive: true },
  { name: 'Upload career documents', free: '5 docs', pro: '25 docs', premium: '100 docs', executive: 'Unlimited' },
  { name: 'AI chat for revisions', free: '10 msgs/month', pro: '100 msgs/month', premium: '500/month', executive: 'Unlimited' },
  { name: 'AI career interview', free: '1 session', pro: '5/month', premium: 'Unlimited', executive: 'Unlimited' },
  { name: 'AI mock interview (voice + 3D avatar)', free: '1/month', pro: '5/month', premium: '20/month', executive: 'Unlimited' },
  { name: 'Panel interviews (multi-avatar)', free: false, pro: true, premium: true, executive: true },
  { name: 'Company-specific interview profiles', free: false, pro: true, premium: true, executive: true },
  { name: 'Real-time interview scoring', free: false, pro: true, premium: true, executive: true },
  { name: 'Interview prep guides', free: true, pro: true, premium: true, executive: true },
  { name: 'Skills gap analysis', free: true, pro: true, premium: true, executive: true },
  { name: 'Job application tracking', free: '10 jobs', pro: 'Unlimited', premium: 'Unlimited', executive: 'Unlimited' },
  { name: 'Follow-up reminders (email)', free: false, pro: true, premium: true, executive: true },
  { name: 'Offer comparison', free: true, pro: true, premium: true, executive: true },
  { name: 'JD red flag detection', free: true, pro: true, premium: true, executive: true },
  { name: 'Email templates', free: 'Basic', pro: 'AI personalization', premium: 'AI personalization', executive: 'AI personalization' },
  { name: 'AI headshot enhancement', free: false, pro: '5/month', premium: '20/month', executive: 'Unlimited' },
  { name: 'Reference management', free: true, pro: true, premium: true, executive: true },
  { name: 'Career timeline', free: true, pro: true, premium: true, executive: true },
  { name: 'LinkedIn optimization suite', free: false, pro: true, premium: true, executive: true },
  { name: 'LinkedIn banner templates', free: false, pro: true, premium: true, executive: true },
  { name: 'LinkedIn section coaching', free: false, pro: true, premium: true, executive: true },
  { name: 'PDF resume templates', free: '1 template', pro: 'All templates', premium: 'All templates', executive: 'All templates' },
  { name: 'Priority support', free: false, pro: true, premium: true, executive: true },
  { name: 'Value Profiles (Executive Suite)', free: false, pro: false, premium: false, executive: true },
  { name: 'Executive-grade documents (Executive Suite)', free: false, pro: false, premium: false, executive: true },
  { name: 'Role playbooks & AI role research (Executive Suite)', free: false, pro: false, premium: false, executive: true },
];

const highlights: { icon: LucideIcon; title: string; desc: string }[] = [
  {
    icon: Target,
    title: 'ATS Match Scoring',
    desc: "See exactly which keywords you match and which you're missing before you apply. Know your fit instantly.",
  },
  {
    icon: Bot,
    title: 'RAG-Powered Personalization',
    desc: 'Your resumes are built from YOUR career history, not generic AI filler. Every bullet point comes from your real experience.',
  },
  {
    icon: ShieldCheck,
    title: 'Claim Verification',
    desc: 'Every claim is checked against your real history after generation — and the AI cannot invent numbers that aren’t in your career data. Included on every plan.',
  },
  {
    icon: FileText,
    title: 'One-Click Cover Letters',
    desc: 'Generate a tailored cover letter that aligns with your resume and the job description in seconds.',
  },
  {
    icon: Mic,
    title: 'AI Mock Interviews',
    desc: 'Practice with 3D talking avatars across 6 round types — including panel interviews. Get real-time scoring and company-specific coaching for 40+ employers.',
  },
  {
    icon: Wand2,
    title: 'Resume Auto-Improve',
    desc: 'One click to fix ATS keyword gaps, strengthen weak bullets, and address missing qualifications — all backed by your career data.',
  },
  {
    icon: SearchCheck,
    title: 'Interview Prep & Gap Analysis',
    desc: 'Get job-specific prep guides with talking points, likely questions, and a skills gap analysis showing where you match and where to improve.',
  },
  {
    icon: Briefcase,
    title: 'Smart Job Management',
    desc: 'Track every application, get follow-up reminders, compare offers side-by-side, and detect red flags in job descriptions.',
  },
  {
    icon: Mail,
    title: 'Professional Emails',
    desc: 'AI-personalized templates for follow-ups, thank-yous, and networking — plus automated reminders so nothing falls through the cracks.',
  },
];

export default function FeaturesContent() {
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-white to-stone-100 py-16 px-4 flex flex-col items-center">
      <main className="w-full max-w-5xl mx-auto">
        {/* Intro */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            Land More Interviews with an AI-Powered Resume
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Beat ATS filters with resumes built from your real career history.
            Practice with AI mock interviewers, track every application, and
            optimize your LinkedIn — all in one platform.
          </p>
          <a
            href="https://app.superpowerresume.com/sign-up"
            className="inline-block bg-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg text-lg hover:bg-blue-700 hover:scale-105 hover:shadow-xl transition-all duration-200"
          >
            Build Your Resume Free
          </a>
          <p className="text-sm text-gray-500 mt-3">No credit card required. Free plan includes 3 resumes/month.</p>
        </div>

        {/* Plan pricing summary */}
        <div className="flex flex-col md:flex-row gap-6 mb-12 justify-center">
          <div className="flex-1 max-w-xs mx-auto md:mx-0 bg-gray-50 rounded-xl p-6 text-center border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Free</h3>
            <p className="text-4xl font-extrabold text-gray-900">$0</p>
            <p className="text-sm text-gray-500 mt-1">No credit card required</p>
          </div>
          <div className="flex-1 max-w-xs mx-auto md:mx-0 bg-blue-50 rounded-xl p-6 text-center border-2 border-blue-500 relative">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-0.5 rounded-full">
              Most Popular
            </span>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Pro</h3>
            <p className="text-4xl font-extrabold text-gray-900">
              $16<span className="text-lg font-normal text-gray-500">/mo</span>
            </p>
            <p className="text-sm text-gray-500 mt-1">billed annually ($19/mo monthly)</p>
          </div>
          <div className="flex-1 max-w-xs mx-auto md:mx-0 bg-gray-50 rounded-xl p-6 text-center border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Premium</h3>
            <p className="text-4xl font-extrabold text-gray-900">
              $33<span className="text-lg font-normal text-gray-500">/mo</span>
            </p>
            <p className="text-sm text-gray-500 mt-1">billed annually ($39/mo monthly)</p>
          </div>
          <div className="flex-1 max-w-xs mx-auto md:mx-0 bg-gray-900 rounded-xl p-6 text-center border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-1">Executive</h3>
            <p className="text-4xl font-extrabold text-white">
              $41<span className="text-lg font-normal text-gray-400">/mo</span>
            </p>
            <p className="text-sm text-gray-400 mt-1">billed annually ($49/mo monthly)</p>
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div className="rounded-2xl shadow-2xl bg-white/90 border border-gray-100 overflow-hidden mb-16">
          <div className="px-6 py-6 border-b border-gray-100">
            <h2 className="text-2xl font-extrabold text-gray-900 text-center">
              Full Feature Comparison
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left font-bold text-gray-800 text-base">
                    Feature
                  </th>
                  <th className="px-6 py-4 text-center font-bold text-gray-800 w-36">
                    Free
                  </th>
                  <th className="px-6 py-4 text-center font-bold text-blue-700 w-36">
                    Pro
                  </th>
                  <th className="px-6 py-4 text-center font-bold text-gray-800 w-36">
                    Premium
                  </th>
                  <th className="px-6 py-4 text-center font-bold text-gray-900 w-36">
                    Executive
                  </th>
                </tr>
              </thead>
              <tbody>
                {features.map(({ name, free, pro, premium, executive }, idx) => (
                  <tr
                    key={name}
                    className={
                      idx % 2 === 0
                        ? 'bg-white hover:bg-blue-50/40'
                        : 'bg-gray-50/50 hover:bg-blue-50/40'
                    }
                  >
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {name}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <FeatureValue value={free} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <FeatureValue value={pro} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <FeatureValue value={premium} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <FeatureValue value={executive} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mid-page CTA */}
        <div className="text-center mb-16">
          <a
            href="https://app.superpowerresume.com/sign-up"
            className="inline-block bg-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg text-lg hover:bg-blue-700 hover:scale-105 hover:shadow-xl transition-all duration-200"
          >
            Get Started Free
          </a>
        </div>

        {/* Feature Highlights */}
        <section className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-20">
          {highlights.map(({ icon: Icon, title, desc }, i) => (
            <ScrollReveal key={title} delay={i * 0.08}>
              <div className="relative group bg-gradient-to-br from-blue-100/80 to-stone-200/80 rounded-2xl p-1 shadow-xl hover:shadow-2xl transition-shadow duration-300">
                <div className="bg-white rounded-2xl p-8 flex flex-col items-center h-full">
                  <div className="-mt-12 mb-4 flex items-center justify-center">
                    <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-lg border-4 border-white group-hover:scale-105 transition-transform">
                      <Icon className="w-9 h-9" />
                    </span>
                  </div>
                  <h3 className="font-extrabold text-xl text-gray-900 mb-3 text-center">
                    {title}
                  </h3>
                  <p className="text-base text-gray-600 text-center">{desc}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </section>

        {/* Final CTA */}
        <section className="flex justify-center">
          <div className="relative bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl shadow-2xl px-8 py-12 max-w-xl w-full flex flex-col items-center">
            <h2 className="text-3xl font-extrabold mb-4 text-center text-white tracking-tight">
              Ready to land more interviews?
            </h2>
            <p className="text-lg text-blue-100 mb-8 text-center">
              Start with the Free plan — no credit card required.
            </p>
            <a
              href="https://app.superpowerresume.com/sign-up"
              className="inline-block bg-white text-blue-700 font-bold py-3 px-8 rounded-xl shadow text-lg hover:bg-blue-50 hover:scale-105 hover:shadow-xl transition-all duration-200"
            >
              Get Started Free
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
